/**
 * GA4 analytics helpers. Uses existing gtag in app.html (G-EJDTK9XKX5).
 * Do not send PII (email, name) in any event or in user_id.
 */

const GA_MEASUREMENT_ID = 'G-EJDTK9XKX5';

declare global {
	interface Window {
		gtag: (...args: unknown[]) => void;
		dataLayer: unknown[];
	}
}

export function gtag(...args: unknown[]) {
	if (typeof window !== 'undefined' && window.gtag) {
		window.gtag(...args);
	}
}

export function initGtag() {
	if (typeof window === 'undefined') return;
	window.dataLayer = window.dataLayer || [];
	gtag('js', new Date());
	gtag('config', GA_MEASUREMENT_ID);
}

/** Set GA4 user_id (non-PII only, e.g. backend user id). Call after login/register; pass null on logout. */
export function setUserId(userId: string | number | null) {
	if (userId == null) {
		gtag('config', GA_MEASUREMENT_ID, { user_id: undefined });
		return;
	}
	gtag('config', GA_MEASUREMENT_ID, { user_id: String(userId) });
}

/** Set user property for segmentation (e.g. logged_in). */
export function setUserProperty(name: string, value: string | boolean | null) {
	if (value == null) return;
	gtag('set', 'user_properties', { [name]: value });
}

// --- Page & navigation ---

export function trackPageView(path: string, title?: string) {
	gtag('event', 'page_view', {
		page_path: path,
		page_title: title ?? (typeof document !== 'undefined' ? document.title : ''),
	});
}

export function trackSelectContent(params: {
	content_type: string;
	content_id: string;
	content_group?: string;
	link_text?: string;
}) {
	gtag('event', 'select_content', params);
}

export function trackViewItemList(params: { item_list_id: string; item_list_name: string }) {
	gtag('event', 'view_item_list', params);
}

// --- Auth & lead ---

export function trackBeginCheckout(params?: { value?: number; currency?: string }) {
	gtag('event', 'begin_checkout', { currency: 'THB', ...params });
}

export function trackGenerateLead(method?: string) {
	gtag('event', 'generate_lead', method ? { method } : {});
}

export function trackLogin(method: string) {
	gtag('event', 'login', { method });
}

export function trackSignUp(method: string) {
	gtag('event', 'sign_up', { method });
}

// --- AI generation ---

export function trackAiGenerationStart(params: {
	prompt_type: 'text' | 'image';
	has_image: boolean;
}) {
	gtag('event', 'ai_model_generation_start', params);
}

export function trackAiGenerationComplete(params: {
	prompt_type: 'text' | 'image';
	task_id: string;
	duration_sec?: number;
}) {
	gtag('event', 'ai_model_generation_complete', params);
}

export function trackViewItem(params: { item_id: string; item_name?: string }) {
	gtag('event', 'view_item', params);
}

export function trackFileDownload(params: {
	file_extension: string;
	file_name: string;
	task_id?: string;
}) {
	gtag('event', 'file_download', params);
}

export function trackPurchase(params: {
	transaction_id: string;
	value: number;
	currency?: string;
	items?: Array<{ item_id: string; item_name: string; price?: number; quantity?: number }>;
}) {
	gtag('event', 'purchase', { currency: 'THB', ...params });
}

export function trackClick(params: { link_url: string; link_text?: string; outbound?: boolean }) {
	gtag('event', 'click', params);
}
